"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import * as React from "react";
import { ShoppingCart, Trash2Icon } from "lucide-react";

import type { getCart, Schemas } from "@fluid-app/fluid";
import {
  addCartItems,
  removeCartItem,
  subscribeCartItem,
  unsubscribeCartItem,
} from "@fluid-app/fluid";
import {
  Button,
  Checkbox,
  ScrollArea,
  Sheet,
  SheetContent,
  SheetFooter,
  SheetTrigger,
} from "@fluid-app/ui";

import type { FluidSDK } from "../utils";
import { QuantityModifier } from "../components/QuantityModifier";
import {
  calculateEstimatedTotals,
  calculateTotalItems,
  calculateTotalSubscriptionSavings,
  CART_SHEET_EVENTS,
  dispatchCartEvent,
  formatDisplayPrice,
  generateWidgetId,
  getAndCacheSDK,
  initializeWithLocalCart,
  waitForSdkReady,
} from "../utils";
import ShadowDOMWrapper from "../utils/ShadowDOMWrapper";
import { useFluidSettings } from "../utils/widgetSettings";

import "../styles/globals.css";

import type { CartWidgetProps } from "@fluid-app/react-widgets-types";

import { defaultMockCart } from "./mockData";

type CartData = Awaited<ReturnType<typeof getCart>> | null;

// Helper function to safely parse a price string to a number
const safeParsePrice = (price?: string): number => {
  if (!price) return 0;

  // Remove any non-numeric characters except decimal point
  const cleanPrice = price.replace(/[^0-9.]/g, "");
  const parsed = parseFloat(cleanPrice);

  // Return 0 if parsing resulted in NaN
  return isNaN(parsed) ? 0 : parsed;
};

// Helper function to identify enrollment pack items
const isEnrollmentPackItem = (item: Schemas["CartItem"]): boolean => {
  return Boolean(item.enrollment);
};

// Helper function to group items by enrollment pack
const groupCartItems = (
  items: Schemas["CartItem"][] | undefined,
  cart: CartData,
) => {
  if (!items || items.length === 0)
    return { regularItems: [], enrollmentPacks: [] };

  // Create a new array to avoid mutating the original items
  const allItems = [...items];

  // Simple separation based on enrollment flag
  const enrollmentItems = allItems.filter(isEnrollmentPackItem);
  const regularItems = allItems.filter((item) => !isEnrollmentPackItem(item));

  // Group enrollment items into a pack
  const enrollmentPacks: {
    id: string | number;
    title: string;
    price: string;
    items: Schemas["CartItem"][];
    firstItem: Schemas["CartItem"];
  }[] = [];

  // If we have no enrollment items, return early
  if (enrollmentItems.length === 0) {
    return { regularItems, enrollmentPacks };
  }

  // Get enrollment pack details directly from the cart if available
  if (cart?.enrollment_pack && enrollmentItems.length > 0) {
    // Ensure we have at least one item to use as firstItem
    // Using definite assignment assertion to satisfy TypeScript
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const firstItem = enrollmentItems[0]!;

    // If we have actual enrollment pack data from the API
    const pack = {
      id: cart.enrollment_pack.enrollment_pack_id ?? "enrollment-pack",
      title: cart.enrollment_pack.title ?? "Enrollment Pack",
      price: cart.enrollment_pack.price_in_currency ?? "0",
      items: enrollmentItems,
      firstItem,
    };

    enrollmentPacks.push(pack);
  } else {
    // Fallback for when enrollment_pack is not available
    const defaultItem = enrollmentItems[0];
    if (defaultItem) {
      // Create a single enrollment pack
      const pack = {
        id: "enrollment-pack",
        title: defaultItem.product_title
          ? `${defaultItem.product_title} Pack`
          : "Enrollment Pack",
        price: "0", // Will be calculated below
        items: enrollmentItems,
        firstItem: defaultItem,
      };

      // Calculate total price for the pack
      const totalPrice = enrollmentItems.reduce((sum, item) => {
        const price = safeParsePrice(item.price_in_currency);
        return sum + price;
      }, 0);

      // Format price for display with proper handling of potential NaN
      const formattedTotalPrice = totalPrice.toFixed(2);
      pack.price = formatDisplayPrice(formattedTotalPrice);

      enrollmentPacks.push(pack);
    }
  }

  return { regularItems, enrollmentPacks };
};

/**
 * Preload cart widget styles
 * This can be called early in the application to ensure styles
 * are ready when components are mounted
 */
export function preloadCartWidgetStyles(): void {
  // Skip in server-side rendering
  if (typeof window === "undefined") return;

  // Use window to check global Fluid settings
  interface FluidWindow extends Window {
    __FLUID_STYLE_URL?: string;
  }
  const fluidWindow = window as unknown as FluidWindow;

  // Make sure we have a style URL set
  fluidWindow.__FLUID_STYLE_URL ??= "/dist/styles.css";

  // Import shadow DOM style utilities and preload styles
  try {
    import("../utils/shadowDomStyles")
      .then((module) => {
        // Create a placeholder shadow root to initialize styles
        const tempDiv = document.createElement("div");
        const tempShadow = tempDiv.attachShadow({ mode: "open" });

        // Apply styles to initialize caching
        module.applyStylesIntoShadowRoot(tempShadow);
      })
      .catch((err) => {
        console.warn("Failed to preload cart widget styles:", err);
      });
  } catch (error) {
    console.warn("Could not import shadow DOM style utilities:", error);
  }
}

// In your CartWidget component, add this to ensure it preloads styles on module load
// This is outside any function or class, at module level
if (typeof window !== "undefined") {
  // Use setTimeout to ensure this runs after hydration
  setTimeout(preloadCartWidgetStyles, 0);
}

export function CartWidget({
  position = "bottom-right",
  size = 60,
  xMargin = 40,
  yMargin = 30,
  onCheckout,
  fluidShopDisplayName,
  hideWidget = false,
  demoMode = false,
  demoCart = null,
}: CartWidgetProps) {
  // Check for server-side rendering early
  if (typeof window === "undefined") {
    return null; // Return null during server-side rendering
  }

  // Use the Fluid settings hook
  const { getDisplayNames } = useFluidSettings();

  // Get the display names with proper priority
  const { shopDisplayName, affiliateDisplayName } =
    getDisplayNames(fluidShopDisplayName);

  // Use the effective shop display name based on priority
  const effectiveShopDisplayName = shopDisplayName;

  // For backwards compatibility, use margin if provided

  // Get initial cart data if available or use demoCart in demo mode
  const initialLocalCart = useMemo(() => {
    if (demoMode) {
      // Use provided demoCart or default mock cart
      return demoCart ?? defaultMockCart;
    }
    return initializeWithLocalCart();
  }, [demoMode, demoCart]);

  // State
  const [isOpen, setIsOpen] = useState(false);
  const [cart, setCart] = useState<CartData>(
    initialLocalCart, // Initialize with local cart data if available
  );
  // Keep a backup of the cart state for potential rollbacks
  const [_cartBackup, setCartBackup] = useState<CartData>(initialLocalCart);

  const [itemCount, setItemCount] = useState(
    initialLocalCart ? calculateTotalItems(initialLocalCart) : 0,
  );

  // Add loading state for cart items
  const [isCartLoading, setIsCartLoading] = useState(false);

  // Add loading state for checkout price
  const [isCheckoutPriceLoading, setIsCheckoutPriceLoading] = useState(false);

  // Create a ref to store the shadow root
  const shadowRootRef = React.useRef<ShadowRoot | null>(null);

  // Track whether we're currently syncing to prevent flickering
  const isSyncingRef = useRef<boolean>(false);

  // Track when the last optimistic update was applied
  const lastOptimisticUpdateTimestampRef = useRef<number>(0);

  // Reference to track the latest operation IDs for each item to handle race conditions
  const itemOperationsRef = useRef<
    Map<
      number,
      { id: number; timestamp: number; type: "increment" | "decrement" }
    >
  >(new Map());

  // SDK reference to ensure we use the same instance consistently
  const sdkRef = useRef<FluidSDK | null>(demoMode ? null : getAndCacheSDK());

  // Generate a stable widget ID once for this component instance
  const widgetIdRef = useRef<string>(generateWidgetId("cart-widget"));

  // Calculate if cart has items
  const hasItems = useMemo(() => !!cart?.items?.length, [cart?.items?.length]);

  // Function to get the current cart with immediate local fallback
  const getCartWithFallback = async (): Promise<CartData> => {
    if (demoMode) return cart;
    if (!sdkRef.current) return null;

    try {
      // First try local cart for immediate feedback
      const localCart = sdkRef.current.getLocalCart();
      if (localCart) {
        return localCart;
      }

      // Then try API cart
      return await sdkRef.current.getCart();
    } catch (e) {
      console.error(
        `Cart widget [${widgetIdRef.current}]: Failed to get cart:`,
        e,
      );
      return null;
    }
  };

  // Get the local cart from SDK and update our state if it's more recent
  const syncWithSdkLocalCart = () => {
    // Skip in demo mode
    if (demoMode) return;

    if (!sdkRef.current) return;

    try {
      const localCart = sdkRef.current.getLocalCart();
      if (!localCart) return;

      // Only update from SDK's local cart if it's newer than our optimistic update
      // or if we don't have any optimistic updates
      const lastCartUpdateStr =
        localStorage.getItem("cart_last_updated") ?? "0";
      const lastCartUpdateTime = parseInt(lastCartUpdateStr, 10);

      // Don't sync if we're in the middle of applying our own optimistic update
      // or if our optimistic update is more recent than the local cart update
      if (
        isSyncingRef.current ||
        lastOptimisticUpdateTimestampRef.current > lastCartUpdateTime
      ) {
        return;
      }

      // Compare with current state to avoid unnecessary re-renders
      // Check if the items in localCart are different from what we have in state
      if (cart && localCart.items && cart.items) {
        const currentTotalItems = calculateTotalItems(cart);
        const localTotalItems = calculateTotalItems(localCart);

        // Get numeric values from sub_total_in_currency for comparison
        const getCurrentCartTotal = () => {
          if (!cart.sub_total_in_currency) return 0;
          const match = /[0-9]+\.?[0-9]*/.exec(cart.sub_total_in_currency);
          return match ? parseFloat(match[0]) : 0;
        };

        const getLocalCartTotal = () => {
          if (!localCart.sub_total_in_currency) return 0;
          const match = /[0-9]+\.?[0-9]*/.exec(localCart.sub_total_in_currency);
          return match ? parseFloat(match[0]) : 0;
        };

        const currentCartTotal = getCurrentCartTotal();
        const localCartTotal = getLocalCartTotal();

        // Update if either the item count or total amount changed
        // This catches price changes due to subscription toggles
        if (
          currentTotalItems !== localTotalItems ||
          Math.abs(currentCartTotal - localCartTotal) > 0.01
        ) {
          setCart(localCart);
          setItemCount(localTotalItems);
        }
      } else if (!cart) {
        // If we don't have a cart but local storage does, use it
        setCart(localCart);
        setItemCount(calculateTotalItems(localCart));
      }
    } catch (e) {
      console.error(
        `Cart widget [${widgetIdRef.current}]: Error syncing with local cart:`,
        e,
      );
    }
  };

  // Initial cart load
  useEffect(
    () => {
      // Skip SDK initialization in demo mode
      if (demoMode) return;

      // Generate a stable log prefix
      const logPrefix = `Cart widget [${widgetIdRef.current}]`;

      // Define the main initialization function
      const initializeCartWidget = async () => {
        try {
          // Wait for the SDK to be ready before proceeding
          await waitForSdkReady();

          // Now it's safe to use SDK functions
          sdkRef.current = getAndCacheSDK();

          // Sync with local cart first for immediate feedback
          syncWithSdkLocalCart();

          // Then fetch from API for complete data
          const freshCart = await getCartWithFallback();

          if (freshCart) {
            setCart(freshCart);
            setItemCount(calculateTotalItems(freshCart));
            setCartBackup(freshCart);
            localStorage.setItem("cart_last_updated", Date.now().toString());
          }

          // Set up event listeners for cart updates from other components
          const handleCartUpdate = (event: Event) => {
            // Properly type the custom event
            const cartEvent = event as CustomEvent<
              | {
                  handledByWidget: boolean;
                  widgetId: string;
                }
              | null
              | undefined
            >;

            // Check if this is our own event to prevent loops
            const eventDetail = cartEvent.detail;
            if (
              eventDetail &&
              eventDetail.handledByWidget &&
              eventDetail.widgetId === widgetIdRef.current
            ) {
              return;
            }

            // If another component updated the cart, we need to sync
            // Add a small delay to avoid updating while other components are still updating
            setTimeout(() => {
              if (!isSyncingRef.current) {
                syncWithSdkLocalCart();
              }
            }, 300);
          };

          window.addEventListener("ADD_TO_CART", handleCartUpdate);
          window.addEventListener("UPDATE_CART_ITEM", handleCartUpdate);
          window.addEventListener("REMOVE_FROM_CART", handleCartUpdate);
          window.addEventListener("LOCALE_CHANGE", handleCartUpdate);

          // Set up a polling interval for cart updates
          const syncInterval = setInterval(() => {
            if (!isSyncingRef.current) {
              // When we don't have a cart yet, check frequently
              if (!cart?.items || cart.items.length === 0) {
                syncWithSdkLocalCart();
              }
              // For carts with items, only sync if it's been a while since our last optimistic update
              else if (
                Date.now() - lastOptimisticUpdateTimestampRef.current >
                2000
              ) {
                syncWithSdkLocalCart();
              }
            }
          }, 1000); // Check every second

          // Store event cleanup functions
          const removeEventListeners = () => {
            window.removeEventListener("ADD_TO_CART", handleCartUpdate);
            window.removeEventListener("UPDATE_CART_ITEM", handleCartUpdate);
            window.removeEventListener("REMOVE_FROM_CART", handleCartUpdate);
            window.removeEventListener("LOCALE_CHANGE", handleCartUpdate);
          };

          // Store the interval cleanup
          const clearIntervalFunction = () => clearInterval(syncInterval);

          // Return both cleanup functions
          return {
            removeEventListeners,
            clearIntervalFunction,
          };
        } catch (error) {
          console.error(
            `${logPrefix}: Failed to initialize cart widget:`,
            error,
          );
          return null;
        }
      };

      // Start initialization and create cleanup reference
      let cleanup: {
        removeEventListeners: () => void;
        clearIntervalFunction: () => void;
      } | null = null;

      // Start the initialization process
      initializeCartWidget()
        .then((result) => {
          cleanup = result;
        })
        .catch((error) => {
          console.error(`Failed to initialize cart widget:`, error);
        });

      // Return a cleanup function that uses the reference
      return () => {
        if (cleanup) {
          cleanup.removeEventListeners();
          cleanup.clearIntervalFunction();
        }
      };
    },
    [demoMode], // Add demoMode as a dependency
  );

  // Listen for cart sheet control events
  useEffect(() => {
    const handleOpenCart = () => {
      setIsOpen(true);
    };

    const handleCloseCart = () => {
      setIsOpen(false);
    };

    const handleToggleCart = () => {
      setIsOpen((prev) => !prev);
    };

    // Add event listeners for cart sheet events
    window.addEventListener(CART_SHEET_EVENTS.OPEN_CART, handleOpenCart);
    window.addEventListener(CART_SHEET_EVENTS.CLOSE_CART, handleCloseCart);
    window.addEventListener(CART_SHEET_EVENTS.TOGGLE_CART, handleToggleCart);

    // Clean up by removing event listeners
    return () => {
      window.removeEventListener(CART_SHEET_EVENTS.OPEN_CART, handleOpenCart);
      window.removeEventListener(CART_SHEET_EVENTS.CLOSE_CART, handleCloseCart);
      window.removeEventListener(
        CART_SHEET_EVENTS.TOGGLE_CART,
        handleToggleCart,
      );
    };
  }, []);

  // Listen for cart loading events
  useEffect(() => {
    const handleCartLoadingStart = () => {
      setIsCartLoading(true);
    };

    const handleCartLoadingEnd = () => {
      // Add a small delay to ensure the cart has time to update
      setTimeout(() => {
        setIsCartLoading(false);
      }, 500);
    };

    // Add event listeners for cart loading events
    window.addEventListener(
      CART_SHEET_EVENTS.CART_LOADING_START,
      handleCartLoadingStart,
    );
    window.addEventListener(
      CART_SHEET_EVENTS.CART_LOADING_END,
      handleCartLoadingEnd,
    );

    // Clean up event listeners
    return () => {
      window.removeEventListener(
        CART_SHEET_EVENTS.CART_LOADING_START,
        handleCartLoadingStart,
      );
      window.removeEventListener(
        CART_SHEET_EVENTS.CART_LOADING_END,
        handleCartLoadingEnd,
      );
    };
  }, []);

  // Handler for incrementing item quantity with optimistic updates
  const handleIncrement = (variantId: number): void => {
    if (!cart?.items) return;

    isSyncingRef.current = true;
    const now = Date.now();
    lastOptimisticUpdateTimestampRef.current = now;

    // Only find non-enrollment items with the matching variant_id
    const item = cart.items.find(
      (i: Schemas["CartItem"]) =>
        i.variant?.id === variantId && !isEnrollmentPackItem(i),
    );
    if (!item) {
      isSyncingRef.current = false;
      return;
    }
    const currentQuantity = Number(item.quantity) || 0;
    const operationId = now;
    itemOperationsRef.current.set(variantId, {
      id: operationId,
      timestamp: now,
      type: "increment",
    });
    const previousCart = { ...cart };
    setCartBackup(previousCart);

    // Optimistic UI update - only update non-enrollment items
    const updatedItems = cart.items.map((i: Schemas["CartItem"]) =>
      i.variant?.id === variantId && !isEnrollmentPackItem(i)
        ? { ...i, quantity: currentQuantity + 1 }
        : i,
    );
    const optimisticCart = calculateEstimatedTotals(cart, updatedItems);
    setCart(optimisticCart);
    setItemCount(calculateTotalItems(optimisticCart));

    // If demo mode, skip SDK calls and just update the state
    if (demoMode) {
      // Dispatch local event for tracking in demo mode
      dispatchCartEvent(
        "UPDATE_CART_ITEM",
        {
          itemId: variantId,
          quantity: currentQuantity + 1,
          action: "increment",
          itemDetails: item,
          timestamp: new Date(now).toISOString(),
          demoMode: true,
        },
        widgetIdRef.current,
      );

      // Simulate delay to match the real-world experience
      setTimeout(() => {
        isSyncingRef.current = false;
      }, 300);
      return;
    }

    localStorage.setItem("cart_last_updated", now.toString());

    // Optional: Dispatch local event (using original action type for clarity?)
    dispatchCartEvent(
      "UPDATE_CART_ITEM",
      {
        itemId: variantId,
        quantity: currentQuantity + 1,
        action: "increment",
        itemDetails: item,
        timestamp: new Date(now).toISOString(),
      },
      widgetIdRef.current,
    );

    setTimeout(() => {
      void (async () => {
        try {
          // Call renamed facade function addCartItems (which calls internal increment)
          // We've already filtered for non-enrollment items in the UI updates
          await addCartItems(variantId, { quantity: 1 });
        } catch (error: unknown) {
          // Catch errors
          const currentOp = itemOperationsRef.current.get(variantId);
          if (
            !currentOp ||
            currentOp.id !== operationId ||
            currentOp.type !== "increment"
          ) {
            return; // Stale operation
          }
          // Log safely
          const errorMessage =
            error instanceof Error ? error.message : String(error); // Rely on instanceof check
          console.error(
            `Cart widget [${widgetIdRef.current}]: Failed SDK add/increment for item ${variantId}:`,
            errorMessage,
          );
          // Revert optimistic UI update
          setCart(previousCart);
          setItemCount(calculateTotalItems(previousCart));
        } finally {
          // Use finally block for cleanup
          // Only clear syncing flag if this is the latest operation we know about for this item
          const currentOp = itemOperationsRef.current.get(variantId);
          if (currentOp?.id === operationId) {
            setTimeout(() => {
              isSyncingRef.current = false;
            }, 800);
          }
        }
      })();
    }, 100);
  };

  // Handler for decrementing item quantity with optimistic updates
  const handleDecrement = (variantId: number): void => {
    if (!cart?.items) return;

    // Only find non-enrollment items with the matching variant_id
    const item = cart.items.find(
      (i: Schemas["CartItem"]) =>
        i.variant?.id === variantId && !isEnrollmentPackItem(i),
    );
    if (!item) return;
    const currentQuantity = Number(item.quantity) || 0;
    if (currentQuantity <= 0) return;

    isSyncingRef.current = true;
    const now = Date.now();
    lastOptimisticUpdateTimestampRef.current = now;
    const operationId = now;
    itemOperationsRef.current.set(variantId, {
      id: operationId,
      timestamp: now,
      type: "decrement",
    });
    const previousCart = { ...cart };
    setCartBackup(previousCart);

    // Optimistic UI update
    const isRemoval = currentQuantity === 1;
    const newQuantity = currentQuantity - 1;
    let updatedItems;
    if (isRemoval) {
      // Only remove the non-enrollment item, keep enrollment items untouched
      updatedItems = cart.items.filter(
        (i: Schemas["CartItem"]) =>
          i.variant?.id !== variantId || isEnrollmentPackItem(i),
      );
    } else {
      // Only decrement non-enrollment items
      updatedItems = cart.items.map((i: Schemas["CartItem"]) =>
        i.variant?.id === variantId && !isEnrollmentPackItem(i)
          ? { ...i, quantity: newQuantity }
          : i,
      );
    }
    const optimisticCart = calculateEstimatedTotals(cart, updatedItems);
    setCart(optimisticCart);
    setItemCount(calculateTotalItems(optimisticCart));

    // If demo mode, skip SDK calls
    if (demoMode) {
      // Dispatch local event for tracking in demo mode
      dispatchCartEvent(
        isRemoval ? "REMOVE_FROM_CART" : "UPDATE_CART_ITEM",
        {
          itemId: isRemoval ? item.id : variantId,
          quantity: isRemoval ? undefined : newQuantity,
          action: "decrement",
          itemDetails: item,
          timestamp: new Date(now).toISOString(),
          demoMode: true,
        },
        widgetIdRef.current,
      );

      // Simulate delay to match the real-world experience
      setTimeout(() => {
        isSyncingRef.current = false;
      }, 300);
      return;
    }

    localStorage.setItem("cart_last_updated", now.toString());

    // Optional: Dispatch local event (using original action type?)
    dispatchCartEvent(
      isRemoval ? "REMOVE_FROM_CART" : "UPDATE_CART_ITEM",
      {
        itemId: isRemoval ? item.id : variantId,
        quantity: isRemoval ? undefined : newQuantity,
        action: "decrement",
        itemDetails: item,
        timestamp: new Date(now).toISOString(),
      },
      widgetIdRef.current,
    );

    setTimeout(() => {
      void (async () => {
        try {
          // Call renamed facade function removeCartItem (which calls internal decrement)
          // We've already filtered for non-enrollment items in the UI updates
          await removeCartItem(variantId, { quantity: 1 });
        } catch (error: unknown) {
          // Catch errors
          const currentOp = itemOperationsRef.current.get(variantId);
          if (
            !currentOp ||
            currentOp.id !== operationId ||
            currentOp.type !== "decrement"
          ) {
            return; // Stale operation
          }
          // Log safely
          const errorMessage =
            error instanceof Error ? error.message : String(error); // Rely on instanceof check
          console.error(
            `Cart widget [${widgetIdRef.current}]: Failed SDK remove/decrement for item ${variantId}:`,
            errorMessage,
          );
          // Revert optimistic UI update
          setCart(previousCart);
          setItemCount(calculateTotalItems(previousCart));
        } finally {
          // Use finally block for cleanup
          // Only clear syncing flag if this is the latest operation we know about for this item
          const currentOp = itemOperationsRef.current.get(variantId);
          if (currentOp?.id === operationId) {
            setTimeout(() => {
              isSyncingRef.current = false;
            }, 800);
          }
        }
      })();
    }, 100);
  };

  // Handle checkout
  const handleCheckout = async (): Promise<void> => {
    try {
      // Set loading state to true when checkout starts
      setIsCheckoutPriceLoading(true);

      // If custom onCheckout is provided, use it
      if (onCheckout) {
        onCheckout();
        // Reset loading state after executing the custom onCheckout
        setIsCheckoutPriceLoading(false);
        return;
      }

      // In demo mode, just log the checkout action
      if (demoMode) {
        console.log("Demo mode: Checkout initiated with", {
          cartId: cart?.id,
          cartTotal: cart?.sub_total_in_currency,
          itemCount,
        });

        // Dispatch event for tracking in demo mode
        dispatchCartEvent(
          "CHECKOUT_INITIATED",
          {
            cartId: cart?.id,
            cartTotal: cart?.sub_total_in_currency,
            itemCount,
            timestamp: new Date().toISOString(),
            demoMode: true,
          },
          widgetIdRef.current,
        );

        // Show a demo message to the user that would normally be redirected
        alert(
          "Demo Mode: In production, you would be redirected to the checkout page now.",
        );
        // Reset loading state in demo mode
        setIsCheckoutPriceLoading(false);
        return;
      }

      if (!sdkRef.current) {
        throw new Error("SDK not initialized");
      }

      // Track checkout initiated event
      dispatchCartEvent(
        "CHECKOUT_INITIATED",
        {
          cartId: cart?.id,
          cartTotal: cart?.sub_total_in_currency,
          itemCount,
        },
        widgetIdRef.current,
      );

      // Call the SDK's checkout function which will handle redirection
      await sdkRef.current.checkout();

      // Note: The checkout function doesn't return a URL, it redirects automatically
      // If we reach this point, the redirection might have been blocked
    } catch (error) {
      console.error(
        `Cart widget [${widgetIdRef.current}]: Failed to checkout:`,
        error,
      );
      // Set loading state to false on error
      setIsCheckoutPriceLoading(false);
    } finally {
      // Reset loading state in case redirection hasn't happened
      // or the checkout process has stalled
      setTimeout(() => {
        setIsCheckoutPriceLoading(false);
      }, 5000); // Add a timeout as a safeguard if redirection is delayed
    }
  };

  // Position classes for the cart widget (for backwards compatibility)
  const positionClasses = {
    "top-left": "top-0 left-0",
    "top-right": "top-0 right-0",
    "bottom-left": "bottom-0 left-0",
    "bottom-right": "bottom-0 right-0",
  };

  return (
    <ShadowDOMWrapper shadowRootRef={shadowRootRef}>
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        {!hideWidget && (
          <SheetTrigger
            className={`fixed border-none ${positionClasses[position]} widget-slide-in z-30 flex translate-y-0 transform cursor-pointer items-center justify-center rounded-full bg-primary text-white shadow-md transition-transform duration-300 ease-in-out`}
            style={{
              width: `${size}px`,
              height: `${size}px`,
              margin: `${yMargin}px ${xMargin}px`,
            }}
            aria-label="Open cart"
            data-fluid-trigger="true"
            icon={
              <div className="relative flex items-center justify-center">
                {/* Cart icon */}
                <ShoppingCart className="h-6 w-6" />

                {/* Item count badge */}
                {itemCount > 0 && (
                  <span className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-white text-xs font-bold text-primary">
                    {itemCount}
                  </span>
                )}
              </div>
            }
          />
        )}

        <SheetContent
          className="fixed bottom-0 right-0 top-0 z-[9999] flex h-full w-full max-w-md flex-col overflow-hidden bg-background p-4 shadow-lg"
          side="right"
          containerRoot={shadowRootRef.current ?? undefined}
        >
          {/* Header - matching original design */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Your cart from</p>
              <div className="text-[18px] font-bold text-foreground">
                {effectiveShopDisplayName}
              </div>
            </div>
          </div>

          {/* Checkout button - matching original design */}
          <Button
            onClick={handleCheckout}
            disabled={!hasItems || isCartLoading || isCheckoutPriceLoading}
            variant="default"
            size={"lg"}
            className="flex h-12 w-full items-center justify-center rounded-sm bg-primary font-semibold text-white sm:h-10"
            isLoading={isCartLoading || isCheckoutPriceLoading}
          >
            {!isCheckoutPriceLoading && !isCartLoading
              ? cart?.sub_total_in_currency
                ? `${formatDisplayPrice(cart.sub_total_in_currency)} - Checkout`
                : "$0.00"
              : null}
          </Button>

          <ScrollArea className="mt-4 flex-1 overflow-y-auto">
            {isCartLoading ? (
              <div className="flex h-full w-full items-center justify-center py-10 text-center text-muted-foreground">
                Loading cart...
              </div>
            ) : !cart || !hasItems ? (
              <div className="px-4 py-10 text-center text-muted-foreground">
                Your cart is empty
              </div>
            ) : (
              <div>
                {/* Group items by enrollment pack */}
                {(() => {
                  const { regularItems, enrollmentPacks } = groupCartItems(
                    cart.items,
                    cart,
                  );

                  return (
                    <>
                      {/* Render enrollment packs first */}
                      {enrollmentPacks.map((pack, packIndex) => (
                        <div
                          key={`pack-${pack.id}`}
                          className={`mb-4 pb-4 ${
                            packIndex < enrollmentPacks.length - 1 ||
                            regularItems.length > 0
                              ? "border-b border-border/10"
                              : ""
                          }`}
                        >
                          {/* Enrollment Pack Header */}
                          <div className="mb-2 flex items-start">
                            {/* Pack Image */}
                            <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-sm">
                              <img
                                src={
                                  pack.firstItem.variant?.image_url ??
                                  pack.firstItem.product?.image_url ??
                                  "https://placehold.co/60x60?text=Pack"
                                }
                                alt={pack.title}
                                className="h-full w-full object-cover"
                              />
                            </div>

                            {/* Pack Details */}
                            <div className="flex flex-1 flex-col px-3">
                              <div className="font-semibold text-foreground">
                                {pack.title}
                              </div>
                              <div className="-mt-0.5 mb-0.5 text-xs text-muted-foreground">
                                (Enrollment Pack)
                              </div>
                              <p className="text-sm font-semibold text-foreground">
                                {formatDisplayPrice(pack.price)}
                              </p>
                            </div>

                            {/* Remove button for entire pack */}
                            <div className="ml-2 self-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => {
                                  // Handle complete removal of enrollment pack
                                  isSyncingRef.current = true;
                                  const now = Date.now();
                                  lastOptimisticUpdateTimestampRef.current =
                                    now;
                                  const previousCart = { ...cart };
                                  setCartBackup(previousCart);

                                  // Since there can only be one enrollment pack in the cart,
                                  // we can simply filter out all items with enrollment=true
                                  const updatedItems = cart.items?.filter(
                                    (i) => !i.enrollment,
                                  );

                                  const optimisticCart =
                                    calculateEstimatedTotals(
                                      cart,
                                      updatedItems,
                                    );
                                  setCart(optimisticCart);
                                  setItemCount(
                                    calculateTotalItems(optimisticCart),
                                  );

                                  // If in demo mode, skip SDK calls
                                  if (demoMode) {
                                    // Dispatch local event for tracking in demo mode
                                    dispatchCartEvent(
                                      "REMOVE_FROM_CART",
                                      {
                                        itemId: pack.firstItem.id,
                                        action: "remove-enrollment-pack",
                                        itemDetails: pack.firstItem,
                                        timestamp: new Date(now).toISOString(),
                                        demoMode: true,
                                      },
                                      widgetIdRef.current,
                                    );

                                    // Simulate a brief delay to match real-world experience
                                    setTimeout(() => {
                                      isSyncingRef.current = false;
                                    }, 300);
                                    return;
                                  }

                                  // Remove the item using the variant_id from the first item
                                  setTimeout(() => {
                                    void (async () => {
                                      try {
                                        if (sdkRef.current) {
                                          // Use the first item's variant_id to remove the pack
                                          await removeCartItem(
                                            pack.firstItem.variant?.id ?? 0,
                                          );
                                        }
                                      } catch (error) {
                                        console.error(
                                          `Cart widget [${widgetIdRef.current}]: Failed to remove enrollment pack:`,
                                          error,
                                        );
                                        setCart(previousCart);
                                        setItemCount(
                                          calculateTotalItems(previousCart),
                                        );
                                      } finally {
                                        setTimeout(() => {
                                          isSyncingRef.current = false;
                                        }, 800);
                                      }
                                    })();
                                  }, 100);
                                }}
                              >
                                <Trash2Icon className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>

                          {/* Sub-items */}
                          <div className="ml-6 border-l border-border/10 pl-4">
                            {pack.items.map((item, itemIndex) => (
                              <div
                                key={`subitem-${item.id ?? `variant-${item.variant?.id}`}`}
                                className={`flex items-center py-2 ${
                                  itemIndex < pack.items.length - 1
                                    ? "border-b border-border/10"
                                    : ""
                                }`}
                              >
                                {/* Small thumbnail */}
                                <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-sm">
                                  <img
                                    src={
                                      item.variant?.image_url ??
                                      item.product?.image_url ??
                                      "https://placehold.co/60x60?text=Product"
                                    }
                                    alt={item.product_title ?? "Product"}
                                    className="h-full w-full object-cover"
                                  />
                                </div>

                                {/* Item title */}
                                <div className="flex-1 px-3">
                                  <div className="text-sm text-foreground">
                                    {item.product_title}
                                  </div>
                                </div>

                                {/* Quantity (only display if more than 1) */}
                                {Number(item.quantity) > 1 && (
                                  <div className="text-sm text-muted-foreground">
                                    x {Number(item.quantity)}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}

                      {/* Render regular items after enrollment packs */}
                      {regularItems.map((item, index) => (
                        <div
                          key={item.id ?? `variant-${item.variant?.id}`}
                          className={`mb-4 flex items-start pb-4 ${
                            index < regularItems.length - 1
                              ? "border-b border-border/10"
                              : ""
                          }`}
                        >
                          {/* Product Image */}
                          <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-sm">
                            <img
                              src={
                                item.variant?.image_url ??
                                item.product?.image_url ??
                                "https://placehold.co/60x60?text=Product"
                              }
                              alt={item.product_title ?? "Product"}
                              className="h-full w-full object-cover"
                            />
                          </div>

                          {/* Product Details */}
                          <div className="flex flex-1 flex-col px-3">
                            <div className="font-semibold text-foreground">
                              {item.product_title}
                            </div>
                            <p className="mt-0.5 text-sm font-semibold text-foreground">
                              {item.subscription &&
                              item.subscription_price_in_currency
                                ? formatDisplayPrice(
                                    item.subscription_price_in_currency,
                                  )
                                : formatDisplayPrice(item.price_in_currency)}

                              {item.subscription &&
                                item.subscribe_and_save_for_display && (
                                  <span className="ml-2 text-xs text-green-600">
                                    {calculateTotalSubscriptionSavings(
                                      item.subscribe_and_save_for_display,
                                      Number(item.quantity) || 1,
                                    )}
                                  </span>
                                )}
                            </p>

                            {/* Subscription toggle */}
                            {item.allow_subscription && (
                              <div className="mt-1 flex items-center">
                                <div className="flex items-center">
                                  <Checkbox
                                    id={`subscribe-${item.id}`}
                                    checked={!!item.subscription}
                                    onCheckedChange={(checked) => {
                                      if (!item.id) return;

                                      // Track that we're updating this item
                                      isSyncingRef.current = true;
                                      const now = Date.now();
                                      lastOptimisticUpdateTimestampRef.current =
                                        now;

                                      // Set checkout price to loading state for both subscribe and unsubscribe actions
                                      setIsCheckoutPriceLoading(true);
                                      // Save current cart state for potential rollback
                                      const previousCart = { ...cart };
                                      setCartBackup(previousCart);

                                      // Store the original subscribe_and_save_for_display value if we don't have it yet
                                      // We'll use it for both states to maintain consistency
                                      const savingsDisplay =
                                        item.subscribe_and_save_for_display ??
                                        "";

                                      // Store the original base savings (per unit) in a data attribute
                                      if (
                                        savingsDisplay &&
                                        !item.subscription
                                      ) {
                                        // When subscribing, we want to save the original base savings format
                                        localStorage.setItem(
                                          `base_savings_${item.id}`,
                                          savingsDisplay,
                                        );
                                      }

                                      // Get the base savings display for this item
                                      const baseSavingsDisplay =
                                        localStorage.getItem(
                                          `base_savings_${item.id}`,
                                        ) ?? savingsDisplay;

                                      // Apply optimistic update for the item price only
                                      if (cart.items) {
                                        const updatedItems = cart.items.map(
                                          (i) => {
                                            if (i.id === item.id) {
                                              // Calculate price based on subscription status and subscribe_and_save
                                              // For optimistic update of the UI, immediately change subscription status and display
                                              return {
                                                ...i,
                                                subscription: !!checked,
                                                subscribe_and_save_for_display:
                                                  baseSavingsDisplay,
                                              };
                                            }
                                            return i;
                                          },
                                        );

                                        // Immediate updates for both subscribing and unsubscribing
                                        // Just update the items for UI display - total will show loading state
                                        setCart((prev) => {
                                          if (!prev) return null;
                                          return {
                                            ...prev,
                                            items: updatedItems,
                                          };
                                        });

                                        // Don't update localStorage in demo mode
                                        if (!demoMode) {
                                          localStorage.setItem(
                                            "cart_last_updated",
                                            now.toString(),
                                          );
                                        }
                                      }

                                      // Skip API calls and event dispatch in demo mode
                                      if (demoMode) {
                                        // Simulate API delay then update the cart with new subscription status
                                        setTimeout(() => {
                                          if (cart.items) {
                                            const updatedItems = cart.items.map(
                                              (i) => {
                                                if (i.id === item.id) {
                                                  return {
                                                    ...i,
                                                    subscription: !!checked,
                                                    subscribe_and_save_for_display:
                                                      baseSavingsDisplay,
                                                  };
                                                }
                                                return i;
                                              },
                                            );

                                            // Update the cart with the new subscription status
                                            const updatedCart = {
                                              ...cart,
                                              items: updatedItems,
                                              // Recalculate the total based on subscription status
                                              sub_total_in_currency:
                                                calculateEstimatedTotals(
                                                  cart,
                                                  updatedItems,
                                                )?.sub_total_in_currency,
                                            };

                                            setCart(updatedCart);
                                            setIsCheckoutPriceLoading(false);
                                            isSyncingRef.current = false;
                                          }
                                        }, 500);
                                        return;
                                      }

                                      // Dispatch event for the update
                                      dispatchCartEvent(
                                        "UPDATE_CART_ITEM",
                                        {
                                          itemId: item.id,
                                          action: item.subscription
                                            ? "unsubscribe"
                                            : "subscribe",
                                          itemDetails: item,
                                          timestamp: new Date(
                                            now,
                                          ).toISOString(),
                                        },
                                        widgetIdRef.current,
                                      );

                                      // Make the API call
                                      setTimeout(() => {
                                        void (async () => {
                                          try {
                                            let updatedCartData;

                                            if (!checked) {
                                              if (typeof item.id === "number") {
                                                // The unsubscribeCartItem function returns the updated cart
                                                updatedCartData =
                                                  await unsubscribeCartItem(
                                                    item.id,
                                                  );
                                              }
                                            } else {
                                              if (typeof item.id === "number") {
                                                // The subscribeCartItem function returns the updated cart
                                                updatedCartData =
                                                  await subscribeCartItem(
                                                    item.id,
                                                  );
                                              }
                                            }

                                            // Based on the response structure shown, the API returns the cart directly
                                            // or possibly within a nested structure
                                            if (updatedCartData) {
                                              // The response might be directly the cart object
                                              // or it might have a nested cart property
                                              const freshCart =
                                                "cart" in updatedCartData
                                                  ? (updatedCartData.cart as Awaited<
                                                      ReturnType<typeof getCart>
                                                    >)
                                                  : (updatedCartData as Awaited<
                                                      ReturnType<typeof getCart>
                                                    >);

                                              setCart(freshCart);
                                              setItemCount(
                                                calculateTotalItems(freshCart),
                                              );
                                              localStorage.setItem(
                                                "cart_last_updated",
                                                Date.now().toString(),
                                              );
                                            }
                                          } catch (error) {
                                            console.error(
                                              `Cart widget [${widgetIdRef.current}]: Failed to ${
                                                !checked
                                                  ? "unsubscribe from"
                                                  : "subscribe to"
                                              } item ${item.id}:`,
                                              error,
                                            );
                                            // Revert to previous cart state on error
                                            setCart(previousCart);
                                          } finally {
                                            // Turn off loading state for checkout price regardless of outcome
                                            setIsCheckoutPriceLoading(false);
                                            setTimeout(() => {
                                              isSyncingRef.current = false;
                                            }, 800);
                                          }
                                        })();
                                      }, 100);
                                    }}
                                    className="mr-1.5"
                                  />
                                  <label
                                    htmlFor={`subscribe-${item.id}`}
                                    className="cursor-pointer text-xs text-muted-foreground"
                                  >
                                    {item.subscription
                                      ? item.subscription_interval
                                        ? `Every ${item.subscription_interval} ${
                                            item.subscription_interval === 1
                                              ? "month"
                                              : "months"
                                          }`
                                        : "Subscribed"
                                      : item.subscribe_and_save_for_display
                                        ? `${calculateTotalSubscriptionSavings(
                                            item.subscribe_and_save_for_display,
                                            Number(item.quantity) || 1,
                                          )}`
                                        : `Save ${item.subscription_price_in_currency}`}
                                  </label>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Quantity controls for regular items */}
                          <div className="ml-2 self-center">
                            <QuantityModifier
                              quantity={Number(item.quantity) || 0}
                              onIncrement={() =>
                                handleIncrement(item.variant?.id ?? 0)
                              }
                              onRemove={() =>
                                handleDecrement(item.variant?.id ?? 0)
                              }
                            />
                          </div>
                        </div>
                      ))}
                    </>
                  );
                })()}
              </div>
            )}
          </ScrollArea>

          {/* Footer */}
          <SheetFooter className="border-t pt-4 text-center text-sm text-muted-foreground">
            {affiliateDisplayName
              ? `Powered by ${effectiveShopDisplayName} x ${affiliateDisplayName}`
              : `Powered by ${effectiveShopDisplayName}`}
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </ShadowDOMWrapper>
  );
}

export default CartWidget;
